import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet.heat";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { Map as MapIcon, Satellite, Globe2, Flame } from "lucide-react";

// Guard against a well-known crash: leaflet.heat's internal `_redraw()` schedules an
// animation frame that calls `this._map.getSize()` — but the layer's `_map` reference
// may be null if the layer was removed (React strict-mode double-mount, or when the
// map container is being resized/torn down). We monkey-patch once to no-op safely.
if (L.HeatLayer && L.HeatLayer.prototype && !L.HeatLayer.prototype.__fn_guarded) {
  const orig = L.HeatLayer.prototype._redraw;
  L.HeatLayer.prototype._redraw = function () {
    if (!this._map || !this._map.getSize) return;
    const size = this._map.getSize();
    if (!size || size.x === 0 || size.y === 0) return;
    try {
      return orig.apply(this, arguments);
    } catch {
      // Non-fatal — the layer just skips this redraw pass.
    }
  };
  L.HeatLayer.prototype.__fn_guarded = true;
}

function makeIcon(label, pulse = false) {
  return L.divIcon({
    className: "",
    html: `<div class="fn-pin ${pulse ? "fn-pin-pulse" : ""}"><span>${label || ""}</span></div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
  });
}

function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom || map.getZoom());
  }, [center, zoom, map]);
  return null;
}

// Heatmap overlay — renders density glow from listing coordinates.
// Smart behavior: fades gracefully as user zooms in (individual pins take over),
// glows brighter when zoomed out to reveal city-scale hotspots.
function HeatLayer({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return undefined;
    let heat = null;
    let cancelled = false;
    let retryTimer = null;

    const applyZoomStyle = () => {
      if (!heat || !heat._map || !map._loaded) return;
      const size = map.getSize && map.getSize();
      if (!size || size.x === 0 || size.y === 0) return;
      const z = map.getZoom();
      const opacity = z <= 11 ? 0.7 : z <= 13 ? 0.45 : z <= 15 ? 0.22 : 0.08;
      const radius = z <= 11 ? 34 : z <= 13 ? 26 : z <= 15 ? 18 : 10;
      try {
        heat.setOptions({ minOpacity: opacity, radius, blur: Math.max(12, radius - 6) });
      } catch { /* map tearing down */ }
    };

    // leaflet.heat crashes with `getSize() null` if added before the map has real dimensions
    // OR while React strict-mode double-invokes effects. Wait for a valid size, then attach.
    const attach = () => {
      if (cancelled) return;
      const size = map.getSize && map.getSize();
      if (!size || size.x === 0 || size.y === 0) {
        retryTimer = setTimeout(attach, 120);   // retry once container is laid out
        return;
      }
      try {
        heat = L.heatLayer(points, {
          radius: 28,
          blur: 22,
          maxZoom: 17,
          minOpacity: 0.35,
          gradient: { 0.2: "#DDA77B", 0.4: "#E05E36", 0.7: "#c94e2a", 1.0: "#2D5A27" },
        });
        heat.addTo(map);
        applyZoomStyle();
        map.on("zoomend", applyZoomStyle);
      } catch (e) {
        // Non-fatal — pins and base tiles still work.
        console.warn("Heatmap init skipped:", e?.message);
      }
    };
    map.whenReady(() => { if (!cancelled) attach(); });

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      map.off("zoomend", applyZoomStyle);
      if (heat) {
        try { map.removeLayer(heat); } catch { /* already gone */ }
      }
    };
  }, [points, map]);
  return null;
}

// Free tile layers — no API key required.
// Esri World Imagery: high-resolution satellite (down to ~30cm in cities).
// OSM Standard: street map.
// CartoDB Voyager Labels: for hybrid — city/road labels on top of satellite.
const LAYERS = {
  street: {
    label: "Street",
    icon: MapIcon,
    url: "https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png",
    attribution: "© OpenStreetMap contributors",
    maxZoom: 19,
  },
  satellite: {
    label: "Satellite",
    icon: Satellite,
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Imagery © Esri, Maxar, Earthstar Geographics",
    maxZoom: 19,
  },
  hybrid: {
    label: "Hybrid",
    icon: Globe2,
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Imagery © Esri · Labels © CartoDB",
    maxZoom: 19,
    // Extra label overlay applied via a second TileLayer when picked.
    overlayUrl:
      "https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png",
  },
};

const LAYER_STORAGE_KEY = "fn_map_layer";

// Lazy-load the Google Maps renderer only when a public API key is set, otherwise
// keep the Leaflet+OSM fallback (no key needed, ideal for preview + offline dev).
const LazyGoogleMap = React.lazy(() => import("./GoogleMapView"));

export default function MapView(props) {
  const gmapKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (gmapKey && gmapKey.length > 10) {
    return (
      <React.Suspense fallback={<div className="w-full h-full bg-stone-100 flex items-center justify-center text-sm text-stone-500">Loading Google Map…</div>}>
        <LazyGoogleMap {...props} />
      </React.Suspense>
    );
  }
  return <LeafletMapView {...props} />;
}


function LeafletMapView({ center = [19.076, 72.8777], markers = [], height = "100%", zoom = 12, tracking }) {
  const [layerKey, setLayerKey] = useState(() => {
    try {
      const saved = typeof window !== "undefined" && window.localStorage.getItem(LAYER_STORAGE_KEY);
      return saved && LAYERS[saved] ? saved : "street";
    } catch {
      return "street";
    }
  });
  const [heatOn, setHeatOn] = useState(false);
  // Track whether the user has ever explicitly toggled — if not, heatmap auto-follows
  // marker density (many results → on; few → off). Once toggled, we respect the choice.
  const userToggledHeatRef = useRef(false);
  const markerCount = (markers || []).filter(m => m.key !== "me").length;

  useEffect(() => {
    if (userToggledHeatRef.current) return;
    setHeatOn(markerCount >= 5);
  }, [markerCount]);

  const toggleHeat = () => {
    userToggledHeatRef.current = true;
    setHeatOn(v => !v);
  };
  const active = LAYERS[layerKey] || LAYERS.street;

  const switchLayer = (k) => {
    setLayerKey(k);
    try { window.localStorage.setItem(LAYER_STORAGE_KEY, k); } catch { /* ignore */ }
  };

  // Build heatmap points from markers — [lat, lng, intensity 0-1].
  // Higher intensity where multiple markers cluster; we let leaflet.heat do the natural blur.
  const heatPoints = React.useMemo(() => (
    (markers || [])
      .filter(m => typeof m.lat === "number" && typeof m.lng === "number" && m.key !== "me")
      .map(m => [m.lat, m.lng, 0.6])
  ), [markers]);

  return (
    <div style={{ height, width: "100%", minHeight: 300 }} className="fn-map-wrap border border-stone-200 relative">
      {/* Floating layer switcher — always visible in top-right */}
      <div
        className="absolute top-3 right-3 z-[400] bg-white/95 backdrop-blur border border-stone-200 rounded-full shadow-lg p-1 flex items-center gap-1"
        data-testid="map-layer-switcher"
      >
        {Object.entries(LAYERS).map(([k, v]) => {
          const Icon = v.icon;
          const isActive = layerKey === k;
          return (
            <button
              key={k}
              onClick={() => switchLayer(k)}
              title={v.label}
              aria-label={`Switch to ${v.label} map`}
              className={`flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[11px] font-bold transition-colors ${
                isActive
                  ? "bg-[#2D5A27] text-white"
                  : "text-stone-700 hover:bg-stone-100"
              }`}
              data-testid={`map-layer-${k}`}
            >
              <Icon size={12} />
              <span className="hidden sm:inline">{v.label}</span>
            </button>
          );
        })}
        <span className="w-px h-4 bg-stone-200 mx-0.5" />
        <button
          onClick={toggleHeat}
          title="Toggle density heatmap"
          aria-label="Toggle listing density heatmap"
          className={`flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[11px] font-bold transition-colors ${
            heatOn
              ? "bg-[#E05E36] text-white"
              : "text-stone-700 hover:bg-stone-100"
          }`}
          data-testid="map-heatmap-toggle"
        >
          <Flame size={12} />
          <span className="hidden sm:inline">Heat</span>
        </button>
      </div>

      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
        <TileLayer
          key={layerKey}
          attribution={active.attribution}
          url={active.url}
          maxZoom={active.maxZoom}
        />
        {/* Hybrid mode: overlay only the labels on top of the satellite base */}
        {layerKey === "hybrid" && active.overlayUrl && (
          <TileLayer
            key="hybrid-labels"
            url={active.overlayUrl}
            attribution=""
            maxZoom={active.maxZoom}
            opacity={0.9}
          />
        )}
        <Recenter center={center} zoom={zoom} />
        {heatOn && <HeatLayer points={heatPoints} />}
        {markers.map((m, i) => (
          <Marker key={m.key || i} position={[m.lat, m.lng]} icon={makeIcon(m.label || (i + 1), m.pulse)}>
            {m.popup && <Popup>{m.popup}</Popup>}
          </Marker>
        ))}
        {tracking && (
          <>
            <Marker position={[tracking.provider_lat, tracking.provider_lng]} icon={makeIcon("🛵", true)}>
              <Popup>Provider on the way</Popup>
            </Marker>
            <Marker position={[tracking.customer_lat, tracking.customer_lng]} icon={makeIcon("📍")}>
              <Popup>You</Popup>
            </Marker>
          </>
        )}
      </MapContainer>
    </div>
  );
}
